/**
 * Mobile Field Focus Management Effects Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles auto-focus, calculator sync, and field navigation effects
 */

import { useEffect, useRef, RefObject } from "react";
import { TransactionCustomFieldValues } from "../../../../types";

// Local types matching CreateTransactionMobile.tsx
interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

export interface UseMobileFieldFocusEffectsProps {
  transactionType: "expense" | "income" | "transfer";
  loading: boolean;
  accounts: any[];
  isInitialFocus: boolean;
  setIsInitialFocus: (value: boolean) => void;
  formData: FormData;
  transferFormData: TransferFormData;
  focusedField: FocusedField;
  setFocusedField: (field: FocusedField) => void;
  calculatorExpression: string;
  setCalculatorExpression: React.Dispatch<React.SetStateAction<string>>;
  hasEvaluatedRef: RefObject<boolean>;
  isOnOptionalField: boolean;
  amountInputRef: RefObject<HTMLInputElement | null>;
  handleFieldFocus: (field: FocusedField) => void;
  getCalculatorDisplayValue: () => string;
  parseExpressionIntoCalculator: (value: string) => void;
  setCalendarViewDate: React.Dispatch<React.SetStateAction<Date>>;
  bottomPanelRef: RefObject<HTMLDivElement | null>;
  timePickerRef: RefObject<HTMLDivElement | null>;
  /** When true, defer initial focus until transaction is loaded so amount shows existing value */
  isEditMode?: boolean;
  /** Set when edit-mode transaction has been loaded; used with isEditMode to avoid showing 0 */
  editingTransaction?: { id: string; amount?: number } | null;
}

export function useMobileFieldFocusEffects({
  transactionType,
  loading,
  accounts,
  isInitialFocus,
  setIsInitialFocus,
  formData,
  transferFormData,
  focusedField,
  setFocusedField,
  calculatorExpression,
  setCalculatorExpression,
  hasEvaluatedRef,
  isOnOptionalField,
  amountInputRef,
  handleFieldFocus,
  getCalculatorDisplayValue,
  parseExpressionIntoCalculator,
  setCalendarViewDate,
  bottomPanelRef,
  timePickerRef,
  isEditMode = false,
  editingTransaction = null,
}: UseMobileFieldFocusEffectsProps) {
  const prevFocusedFieldRef = useRef<FocusedField>(null);

  // Auto-focus amount field when component loads and data is ready (create mode only).
  // In edit mode we do NOT auto-focus so the keyboard doesn't open by default; user can tap amount to edit.
  useEffect(() => {
    if (isEditMode) return; // Never auto-focus in edit mode
    const dataReady = !loading && accounts.length > 0 && isInitialFocus;
    if (dataReady) {
      const currentAmount = transactionType === "transfer" ? transferFormData.amount : formData.amount;
      setCalculatorExpression(currentAmount || "0");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (amountInputRef.current) {
            amountInputRef.current.focus();
            handleFieldFocus("amount");
            setIsInitialFocus(false);
          }
        });
      });
    }
  }, [loading, accounts.length, isInitialFocus, transactionType, transferFormData.amount, formData.amount, setCalculatorExpression, amountInputRef, handleFieldFocus, setIsInitialFocus, isEditMode]);

  // Edit mode: clear isInitialFocus so the Cancel/Update bar is visible (we don't auto-focus amount in edit mode)
  useEffect(() => {
    if (isEditMode && isInitialFocus) {
      setIsInitialFocus(false);
    }
  }, [isEditMode, isInitialFocus, setIsInitialFocus]);

  // Edit mode: as soon as transaction is loaded and form has amount, sync to calculator so it never shows 0
  // When in edit mode and form has amount but calculator shows 0: restore only if amount field is NOT focused
  // (If amount is focused, user may have just cleared with backspace - do not overwrite)
  useEffect(() => {
    if (!isEditMode || !editingTransaction) return;
    if (focusedField === "amount") return;
    const currentAmount = transactionType === "transfer" ? transferFormData.amount : formData.amount;
    if (!currentAmount || currentAmount === "0") return;
    const displayValue = getCalculatorDisplayValue();
    if (displayValue === "0" || displayValue === "") {
      parseExpressionIntoCalculator(currentAmount);
    }
  }, [isEditMode, editingTransaction, transactionType, focusedField, transferFormData.amount, formData.amount, getCalculatorDisplayValue, parseExpressionIntoCalculator]);

  // Sync calculator value with form when amount or rate changes externally
  // IMPORTANT: Only sync if user is NOT actively typing (calculator value is not longer than formData value)
  useEffect(() => {
    if (focusedField === "amount" && !hasEvaluatedRef.current) {
      const currentAmount = transactionType === "transfer" ? transferFormData.amount : formData.amount;
      const displayValue = getCalculatorDisplayValue();

      // Edit mode: when transaction has just loaded, form has the real amount but calculator may still be "0"
      if (isEditMode && editingTransaction && currentAmount && currentAmount !== "0" && (displayValue === "0" || displayValue === "")) {
        parseExpressionIntoCalculator(currentAmount);
        return;
      }
      
      // Extract numeric parts for length comparison (handles both formatted "8.00" and raw "8")
      // Remove all non-digit characters except decimal point and minus sign for comparison
      const extractNumericLength = (str: string): number => {
        if (!str) return 0;
        // Remove everything except digits, decimal point, and minus sign
        const numeric = str.replace(/[^\d.-]/g, "");
        return numeric.length;
      };
      
      const formAmountLength = extractNumericLength(currentAmount || "");
      const displayValueLength = extractNumericLength(displayValue);
      
      // Check if user is actively typing (calculator value is longer than formData value)
      const isUserTyping = displayValueLength > formAmountLength;
      // Check if calculator has operators (user is building an expression)
      const hasOperators = /[+\-*/]/.test(displayValue);
      
      const formNumeric = (currentAmount || "").replace(/[^\d.-]/g, "");
      const displayNumeric = displayValue.replace(/[^\d.-]/g, "");
      // Only sync if values differ AND user is not actively typing AND no operators
      // Also check if displayValue starts with the formAmount (user is continuing to type)
      const displayStartsWithForm = formNumeric && displayNumeric.startsWith(formNumeric);
      // Do NOT sync when display is "0" but form still has a value - user just cleared with backspace
      const userJustCleared = (displayValue === "0" || displayValue === "") && currentAmount !== "" && currentAmount !== "0";
      // Do NOT sync when display is a prefix of form (e.g. display "123", form "1234") - user is backspacing on iPhone
      const displayIsPrefixOfForm = formNumeric.length > displayNumeric.length && formNumeric.startsWith(displayNumeric);
      // Do NOT sync when display is shorter than form - user cleared/re-typed (e.g. 0 then tap 5; don't overwrite with "1234")
      const displayShorterThanForm = displayValueLength < formAmountLength;
      // CRITICAL: When form amount is empty/zero and calculator has user input (not "0"), NEVER sync
      // This prevents resetting calculator when user selects time first, then types amount
      // This is the key fix for the issue where selecting time first prevents amount input
      const formIsEmptyButCalculatorHasInput = (currentAmount === "" || currentAmount === "0") && displayValue !== "0" && displayValue !== "" && displayValueLength > 0;
      const shouldSync = currentAmount !== displayValue && currentAmount !== "" && !isUserTyping && !hasOperators && !displayStartsWithForm && !userJustCleared && !displayIsPrefixOfForm && !displayShorterThanForm && !formIsEmptyButCalculatorHasInput;
      
      if (shouldSync) {
        parseExpressionIntoCalculator(currentAmount || "0");
      }
    } else if ((focusedField === "rate" || focusedField === "defaultCurrencyRate") && !hasEvaluatedRef.current) {
      const currentRate = focusedField === "rate" ? formData.rate : formData.defaultCurrencyRate;
      const displayValue = getCalculatorDisplayValue();
      // Never overwrite calculator with "0" or empty when user has a value (same as amount: respect backspace/typing)
      if ((currentRate === "" || currentRate === "0") && displayValue !== "" && displayValue !== "0") {
        return;
      }

      // Extract numeric parts for length comparison (same logic as amount for iOS backspace/clear)
      const extractNumericLength = (str: string): number => {
        if (!str) return 0;
        const numeric = str.replace(/[^\d.-]/g, "");
        return numeric.length;
      };

      const formRateLength = extractNumericLength(currentRate || "");
      const displayValueLength = extractNumericLength(displayValue);

      // Check if user is actively typing (calculator value is longer than formData value)
      const isUserTyping = displayValueLength > formRateLength;
      // Check if calculator has operators (user is building an expression)
      const hasOperators = /[+\-*/]/.test(displayValue);

      const formNumeric = (currentRate || "").replace(/[^\d.-]/g, "");
      const displayNumeric = displayValue.replace(/[^\d.-]/g, "");
      // Only sync if values differ AND user is not actively typing AND no operators
      const displayStartsWithForm = formNumeric && displayNumeric.startsWith(formNumeric);
      // Do NOT sync when display is "0" but form still has a value - user just cleared with backspace
      const userJustCleared = (displayValue === "0" || displayValue === "") && currentRate !== "" && currentRate !== "0";
      // Do NOT sync when display is a prefix of form (e.g. display "1.2", form "1.23") - user is backspacing on iPhone
      const displayIsPrefixOfForm = formNumeric.length > displayNumeric.length && formNumeric.startsWith(displayNumeric);
      // Do NOT sync when display is shorter than form - user cleared/re-typed (e.g. 0 then tap 5)
      const displayShorterThanForm = displayValueLength < formRateLength;
      const shouldSync = currentRate !== displayValue && currentRate !== "" && !isUserTyping && !hasOperators && !displayStartsWithForm && !userJustCleared && !displayIsPrefixOfForm && !displayShorterThanForm;

      if (shouldSync) {
        parseExpressionIntoCalculator(currentRate || "0");
      }
    }
    // Reset the flag after the effect runs
    if (focusedField !== "amount") {
      hasEvaluatedRef.current = false;
    }
  }, [transactionType === "transfer" ? transferFormData.amount : formData.amount, focusedField, formData.rate, formData.defaultCurrencyRate, calculatorExpression, getCalculatorDisplayValue, parseExpressionIntoCalculator, hasEvaluatedRef, isEditMode, editingTransaction]);

  // Auto-open account or category selector when amount is entered
  useEffect(() => {
    // Track previous focused field (update first so next run has correct "previous")
    const prevFocused = prevFocusedFieldRef.current;
    prevFocusedFieldRef.current = focusedField;

    // Only for non-transfer transactions
    if (transactionType === "transfer") return;

    // Only if amount field is not currently focused (user finished entering amount)
    if (focusedField === "amount") return;

    // Don't trigger if account or category is already focused (checkmark button might have opened it)
    if (focusedField === "account" || focusedField === "category") return;

    // Don't auto-focus if user is currently interacting with optional fields (note/attachment)
    if (isOnOptionalField) {
      return; // Don't auto-focus when user is typing in note or attachment
    }

    // Check if amount is filled
    const currentAmount = formData.amount;
    const hasAmount = currentAmount && currentAmount.trim() !== ""; // Allow "0" as valid amount
    const hasAccount = formData.accountId && formData.accountId.trim() !== "";
    const hasCategory = formData.category && formData.category.trim() !== "";

    // Auto-open account or category only when user left the AMOUNT field (not when they closed rate/defaultCurrencyRate calculator)
    // So tapping check on exchange rate or default currency rate just closes the calculator and does not open category list
    const didLeaveAmountField = prevFocused === "amount" && focusedField === null;

    if (hasAmount && focusedField === null && didLeaveAmountField) {
      // Small delay to ensure smooth transition and avoid conflicts with checkmark button
      const timer = setTimeout(() => {
        // Double-check the state and that user is not on note/attachment
        if (focusedField === null && !isOnOptionalField) {
          if (!formData.accountId) {
            // Account is not selected, go to account field
            handleFieldFocus("account");
          } else if (!formData.category) {
            // Account is selected but category is not, go to category
            handleFieldFocus("category");
          }
        }
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [formData.amount, formData.accountId, formData.category, transactionType, focusedField, isOnOptionalField, handleFieldFocus]);

  // Sync calendar view with selected date when date field is focused
  useEffect(() => {
    if (focusedField === "date") {
      const currentDate = transactionType === "transfer" ? transferFormData.date : formData.date;
      if (currentDate) {
        setCalendarViewDate(new Date(currentDate));
      }
    }
  }, [focusedField, transactionType, transferFormData.date, formData.date, setCalendarViewDate]);

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (focusedField) {
        const target = event.target as Node;
        // When calculator is open (rate, defaultCurrencyRate, feeAmount, or measurable detail), allow switching to another field by tapping it (data-field)
        const calculatorOpen = focusedField === "rate" || focusedField === "defaultCurrencyRate" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"));
        if (calculatorOpen) {
          const fieldEl = target instanceof HTMLElement ? target.closest?.("[data-field]") : null;
          if (fieldEl) {
            const tappedField = fieldEl.getAttribute("data-field") as FocusedField | null;
            if (tappedField && tappedField !== "amount" && tappedField !== "rate" && tappedField !== "defaultCurrencyRate" && tappedField !== "feeAmount" && tappedField !== "date" && tappedField !== "time") {
              handleFieldFocus(tappedField);
              return;
            }
          }
        }
        // Check if clicking inside bottom panel or time picker
        const isInsidePanel = bottomPanelRef.current?.contains(target);
        const isInsideTimePicker = timePickerRef.current?.contains(target);
        
        // Don't close if clicking inside the panel, time picker, or on the input field
        if (!isInsidePanel && !isInsideTimePicker && 
            !(target instanceof HTMLElement && target.closest('input'))) {
          // Only close if clicking outside both panel and input fields
          const isInputField = (target instanceof HTMLElement) && 
            (target.tagName === 'INPUT' || target.closest('input'));
          if (!isInputField) {
            // Don't close date/time/amount/rate/defaultCurrencyRate/feeAmount/measurable-detail on outside click (they have their own Done buttons / calculator)
            if (focusedField === "date" || focusedField === "time" || focusedField === "amount" || focusedField === "rate" || focusedField === "defaultCurrencyRate" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"))) {
              return;
            }
            // Close other fields when clicking outside
            setFocusedField(null);
          }
        }
      }
    };

    if (focusedField) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [focusedField, calculatorExpression, transactionType, transferFormData, formData, bottomPanelRef, timePickerRef, setFocusedField, handleFieldFocus]);
}
